/* ─────────────────────────────────────────────────────────
 *  TableOps – Built-in Audio Cue Motifs
 *
 *  Tiny, dependency-free `DawMidiFile` motifs played through the
 *  MIDI-DAW synth engine. These are the *fallback* cues used when
 *  no matching MIDI-DAW library element is available — so the
 *  experience works out-of-the-box, yet an operator can author a
 *  richer cue in the MIDI DAW library and it transparently wins.
 *
 *  Single responsibility: motif data generation. No audio, no React.
 * ───────────────────────────────────────────────────────── */
import type { DawMidiFile, DawNote, DawTrack } from '../../midi-daw/types/midi-daw-types'
import { midiToNoteName } from '../../midi-daw/types/midi-daw-types'

interface MotifNote {
  /** MIDI note number (0–127). */
  midi: number
  /** Start offset in seconds. */
  start: number
  /** Duration in seconds. */
  dur: number
  /** Velocity 0–127 (default 100). */
  vel?: number
}

const PPQ = 480
const BPM = 120
const SEC_PER_TICK = 60 / BPM / PPQ

/**
 * Assemble a single-track `DawMidiFile` from a flat note list.
 * Notes are sorted by start time (the scheduler requires ascending order).
 */
function buildMotif(name: string, color: string, notes: MotifNote[]): DawMidiFile {
  const dawNotes: DawNote[] = notes
    .slice()
    .sort((a, b) => a.start - b.start)
    .map((n, i) => ({
      id: `cue-${name}-${i}`,
      midi: n.midi,
      name: midiToNoteName(n.midi),
      ticks: Math.round(n.start / SEC_PER_TICK),
      durationTicks: Math.round(n.dur / SEC_PER_TICK),
      time: n.start,
      duration: n.dur,
      velocity: n.vel ?? 100,
    }))

  const durationSeconds = notes.reduce((max, n) => Math.max(max, n.start + n.dur), 0) + 0.25

  const track: DawTrack = {
    index: 0,
    name,
    channel: 0,
    instrument: name,
    notes: dawNotes,
    color,
    muted: false,
    solo: false,
    volume: 0.9,
    collapsed: false,
    height: 80,
  }

  return {
    fileName: `${name}.cue`,
    ppq: PPQ,
    durationSeconds,
    durationTicks: Math.round(durationSeconds / SEC_PER_TICK),
    bpm: BPM,
    timeSignatureNumerator: 4,
    timeSignatureDenominator: 4,
    tracks: [track],
  }
}

/**
 * Order-enter cue — a bright, welcoming rising arpeggio (C5 · E5 · G5 · C6).
 * Signals "a new order just arrived".
 */
export const CUE_ORDER_ENTER: DawMidiFile = buildMotif('order-enter', '#38bdf8', [
  { midi: 72, start: 0.00, dur: 0.11, vel: 96 },
  { midi: 76, start: 0.09, dur: 0.11, vel: 100 },
  { midi: 79, start: 0.18, dur: 0.11, vel: 104 },
  { midi: 84, start: 0.27, dur: 0.34, vel: 112 },
])

/**
 * Station-close cue — a soft descending two-note confirmation (C6 → G5).
 * Signals "a station finished / closed its ticket".
 */
export const CUE_STATION_CLOSE: DawMidiFile = buildMotif('station-close', '#34d399', [
  { midi: 84, start: 0.00, dur: 0.10, vel: 104 },
  { midi: 79, start: 0.09, dur: 0.30, vel: 96 },
])
